/**
 * A portal service's sync history (#11635 slice 1, Round 11, R34; shown as
 * a section of the service's page since Round 13, R42): the route's wire
 * shape and its read.
 */
import { useQuery } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import type { Temporal } from "@/utils/temporal";
import type { RunWindow } from "./browser/state";

export type PortalRunOutcome = "succeeded" | "failed" | "running" | "cancelled";

/** One sync, as the runs route answers it. */
export interface PortalRun {
  started_at: Temporal;
  /** Null while the run is open. Bounds the run's window (R51); no
   *  duration is drawn from it (Round 21, R71). */
  finished_at: Temporal | null;
  outcome: PortalRunOutcome;
  /** The stored error text, null when none. Kept on the wire for an
   *  engineer; nothing renders it (Round 21, R71). */
  error: string | null;
  dry_run: boolean;
  /** Documents the run retrieved (R43); so far, while it runs. */
  documents: number;
  /** Documents the run tried and could not keep (#13095 R-R1a). */
  rejected: number;
}

export interface PortalRunsResponse {
  runs: PortalRun[];
}

export const portalRunsUrl = (connectionId: number): string =>
  `${BASE_API_URL_V2}/api/v2/connections/portals/credentials/${connectionId}/runs`;

/** A service's runs, newest first (at most 100, the route's bound), read
 *  while the service's page is open. */
export const usePortalRuns = (connectionId: number) =>
  useQuery<PortalRunsResponse>({
    queryKey: ["portal-runs", connectionId],
    queryFn: async ({ signal }) => {
      const response = await fpFetch(portalRunsUrl(connectionId), { signal });
      if (!response.ok) {
        throw new Error(`Failed to load the sync history: ${response.status}`);
      }
      return response.json();
    },
  });

/** The window a run's documents arrived in (Round 15, R51): its start and
 *  end as the wire sends them, `to` null while it is open. Null for a run
 *  whose start is not an instant, which no window can be drawn from. */
export const runWindowOf = (run: PortalRun): RunWindow | null => {
  if (run.started_at.granularity !== "instant") return null;
  const end = run.finished_at;
  return {
    from: run.started_at.at,
    to: end !== null && end.granularity === "instant" ? end.at : null,
  };
};
